
import { firestore } from "@/firebase";
import { PhaseHoursCalculator } from "@/services/PhaseHoursCalculator";
import { ROOT_ACTIONS } from "@/store/actions";
import { USER_GETTERS } from "@/store/user/getters";
import { User } from "@/store/user/types";
import { Activity, ActualActivity, ContentAssignment, Phase, Program, StateType } from "@/types";
import { getMailTemplate } from "@/utils";
import { format } from "date-fns";
import {
  collection,
  getDoc,
  getDocs,
  query,
  where,
  doc,
  orderBy,
  limit,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { Component, Vue, Watch } from "vue-property-decorator";

@Component({
  components: {},
})
export default class Home extends Vue {
  rows = [] as any[];
  showNotes = false;

  get user(): User {
    return this.$store.getters[USER_GETTERS.ACTING_USER];
  }

  get headers() {
    return [
      { text: "Naam", value: "name", groupable: false },
      { text: "Startdatum", value: "startDate", groupable: false },
      { text: "Status", value: "status" },
      { text: "Fase 1", value: "phase1", groupable: false },
      { text: "Fase 2", value: "phase2", groupable: false },
      { text: "Fase 3", value: "phase3", groupable: false },
      { text: "Uren Tot.", value: "totalHours", groupable: false },
      { text: "Aanwzg", value: "present", groupable: false },
      { text: "Bevestigd", value: "confirmed", groupable: false },
      { text: "Niet Bev.", value: "notConfirmed", groupable: false },
      { text: "Coach", value: "coach" },
      this.showNotes
        ? { text: "Notitie", value: "notes", groupable: false }
        : null,
      { text: "Mail", value: "mail", groupable: false },
    ].filter(Boolean);
  }

  loading = true;

  async mounted() {
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, []);

    // Fetch everything in bulk
    const programsQuery = query(
      collection(firestore, "programs"),
      where("isArchived", "!=", true)
    );

    const programsSnapshot = await getDocs(programsQuery);

    const participantsSnapshot = await getDocs(collection(firestore, "users"));
    const coachesSnapshot = await getDocs(collection(firestore, "users"));

    const phasesQuery = query(
      collection(firestore, "phases"),
      orderBy("start", "asc")
    );
    const phasesSnapshot = await getDocs(phasesQuery);

    const activitiesQuery = query(collection(firestore, "activities"));
    const activitiesSnapshot = await getDocs(activitiesQuery);

    const actualActivitiesQuery = query(
      collection(firestore, "actual-activities")
    );
    const actualActivitiesSnapshot = await getDocs(actualActivitiesQuery);

    // Index data for faster lookups
    const participantsMap = new Map();
    participantsSnapshot.docs.forEach((doc) =>
      participantsMap.set(doc.id, doc.data())
    );

    const coachesMap = new Map();
    coachesSnapshot.docs.forEach((doc) => coachesMap.set(doc.id, doc.data()));

    const phasesMap = new Map();
    phasesSnapshot.docs.forEach((doc) => {
      const phaseData = doc.data();
      if (!phasesMap.has(phaseData.programId)) {
        phasesMap.set(phaseData.programId, []);
      }
      phasesMap.get(phaseData.programId).push({ ...phaseData, id: doc.id });
    });

    const activitiesMap = new Map();
    activitiesSnapshot.docs.forEach((doc) => {
      const activityData = doc.data();
      if (!activitiesMap.has(activityData.phaseId)) {
        activitiesMap.set(activityData.phaseId, []);
      }
      activitiesMap
        .get(activityData.phaseId)
        .push({ ...activityData, id: doc.id });
    });

    const actualActivitiesMap = new Map();
    actualActivitiesSnapshot.docs.forEach((doc) => {
      const actualActivityData = doc.data();
      if (!actualActivitiesMap.has(actualActivityData.activityId)) {
        actualActivitiesMap.set(actualActivityData.activityId, []);
      }
      actualActivitiesMap
        .get(actualActivityData.activityId)
        .push({ ...actualActivityData, id: doc.id });
    });

    // Fetch content-assignments, event-attendees, webinars for new hour system
    const contentAssignmentsSnapshot = await getDocs(collection(firestore, "content-assignments"));
    const eventAttendeesSnapshot = await getDocs(collection(firestore, "event-attendees"));
    const webinarsSnapshot = await getDocs(collection(firestore, "webinars"));

    // Index content-assignments by "userId_phaseId"
    const contentAssignmentsMap = new Map<string, any[]>();
    contentAssignmentsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      const key = `${data.user}_${data.phase}`;
      if (!contentAssignmentsMap.has(key)) contentAssignmentsMap.set(key, []);
      contentAssignmentsMap.get(key)!.push({ ...data, id: doc.id });
    });

    // Index event-attendees by userId
    const eventAttendeesMap = new Map<string, any[]>();
    eventAttendeesSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (!eventAttendeesMap.has(data.user)) eventAttendeesMap.set(data.user, []);
      eventAttendeesMap.get(data.user)!.push({ ...data, id: doc.id });
    });

    // Index webinars by id
    const webinarsMap = new Map<string, any>();
    webinarsSnapshot.docs.forEach((doc) => {
      webinarsMap.set(doc.id, { ...doc.data(), id: doc.id });
    });

    // Process programs
    for (const programDoc of programsSnapshot.docs) {
      const programData = programDoc.data();
      if (programData.state === StateType.SUSPENDED) {
        continue;
      }
      const participant = participantsMap.get(programData.participant);
      const coach = coachesMap.get(programData.coach);

      if (!participant || !coach) {
        continue; // Skip if participant or coach doesn't exist
      }

      // const programPhases = phasesMap.get(programDoc.id) || [];
      // programPhases.sort(
      //   (a: any, b: any) => a.start.toDate() - b.start.toDate()
      // );

      // const [firstPhase, secondPhase, thirdPhase] = programPhases;

      // if (!firstPhase) {
      //   continue;
      // }

      // let currentPhase = "Einde traject";
      // const now = new Date();

      // if (firstPhase.start.toDate() < now && firstPhase.end.toDate() > now) {
      //   currentPhase = "Fase 1";
      // } else if (
      //   secondPhase?.start.toDate() < now &&
      //   secondPhase?.end.toDate() > now
      // ) {
      //   currentPhase = "Fase 2";
      // } else if (
      //   thirdPhase?.start.toDate() < now &&
      //   thirdPhase?.end.toDate() > now
      // ) {
      //   currentPhase = "Fase 3";
      // }

      // const phasesToCheck = [firstPhase, secondPhase, thirdPhase].filter(
      //   Boolean
      // );
      // const plannedHoursPhases = Array(phasesToCheck.length).fill(0);

      // const actualHoursPhases = Array(phasesToCheck.length).fill(0);
      // const confirmedHoursPhases = Array(phasesToCheck.length).fill(0);
      // const nonConfirmedHoursPhases = Array(phasesToCheck.length).fill(0);
      // const phaseWarnings = Array(phasesToCheck.length).fill(false);

      // for (const [index, phase] of phasesToCheck.entries()) {
      //   const activitiesForPhase = activitiesMap.get(phase.id) || [];
      //   const activitiesPlanned = activitiesForPhase.reduce(
      //     (acc: any, curr: any) => acc + curr.plannedHours,
      //     0
      //   );

      //   const totalPlannedHoursByAdmin = activitiesForPhase.reduce(
      //     (acc: any, curr: any) => acc + curr.plannedHours,
      //     0
      //   );

      //   plannedHoursPhases[index] += totalPlannedHoursByAdmin;
      //   let plannedHoursByCoach = 0;
      //   for (const activity of activitiesForPhase) {
      //     const actualActivities = actualActivitiesMap.get(activity.id) || [];
      //     // sum all actual hours for actual activities

      //     plannedHoursByCoach += actualActivities.reduce(
      //       (acc: any, curr: any) => acc + curr.actualHours,
      //       0
      //     );
      //     const presentHours = actualActivities
      //       .filter((e: any) => e.participantPresent)
      //       .reduce((acc: any, curr: any) => acc + curr.actualHours, 0);

      //     const confirmedHours = actualActivities
      //       .filter((e: any) => e.participantConfirmed)
      //       .reduce((acc: any, curr: any) => acc + curr.actualHours, 0);

      //     actualHoursPhases[index] += presentHours;
      //     confirmedHoursPhases[index] += plannedHoursByCoach;
      //     nonConfirmedHoursPhases[index] += presentHours - confirmedHours;

      //     if (
      //       totalPlannedHoursByAdmin * 1.2 < presentHours &&
      //       phase.end.toDate() < now
      //     ) {
      //       phaseWarnings[index] = true;
      //     }
      //   }
      // }

      // const totalConfirmedHours = confirmedHoursPhases.reduce(
      //   (acc, curr) => acc + curr,
      //   0
      // );

      // const totalNonConfirmedHours = nonConfirmedHoursPhases.reduce(
      //   (acc, curr) => acc + curr,
      //   0
      // );

      // const totalPresentHours = actualHoursPhases.reduce(
      //   (acc, curr) => acc + curr,
      //   0
      // );

      // //   warning if 20% or more hours are not confirmed
      // const warning = totalNonConfirmedHours / totalPresentHours >= 0.2;

      const isDiverseActivity = (name?: string) => {
        const lower = (name || "").toLowerCase();
        return lower.includes("divers");
      };

      const getActivityPlannedHours = (activity: Activity) => {
        const h = typeof activity.plannedHours === "string"
          ? parseFloat(activity.plannedHours)
          : activity.plannedHours;
        return h || 0;
      };

      const getPhaseBreakdown = (phaseIndex: number) => {
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        if (phaseIndex >= phasesInThisProgram.length) return null;

        const phase = phasesInThisProgram[phaseIndex] as Phase;
        const participantId = programData.participant;
        const phaseActivities: Activity[] = activitiesMap.get(phase.id) || [];

        // Analytics-only: diverse hours are split out of activities and auto-counted at 100%.
        // Coach/admin/participant overviews keep diverse hours inside activities.
        const plannedDiverse = phaseActivities
          .filter((a: Activity) => isDiverseActivity(a.name))
          .reduce((sum: number, a: Activity) => sum + getActivityPlannedHours(a), 0);

        // --- PLANNED HOURS (via PhaseHoursCalculator for consistent logic) ---
        const planned = PhaseHoursCalculator.getPlannedHours(phase, phaseActivities);
        const plannedActivities = Math.max(0, planned.activityHours - plannedDiverse);
        const plannedScreencasts = planned.screencastHours;
        const plannedWebinars = planned.webinarHours;

        // --- ACTUAL HOURS ---
        let actualActivities = 0, actualScreencasts = 0, actualWebinars = 0;
        // Planned diverse hours count as fully completed in analytics (regardless of coach input)
        const actualDiverse = plannedDiverse;

        // New system: screencast/webinar hours from template are counted directly ("Direct geteld")
        const isNewSystem = !!(phase.hours || phase.screencastHours || phase.webinarHours);

        // From activities — for new system phases, skip legacy screencast/webinar named activities
        // (they are replaced by template-based counting and would cause double-counting).
        // Diverse activities are also skipped here (counted via plannedDiverse above).
        phaseActivities.forEach((activity: Activity) => {
          const actuals: ActualActivity[] = actualActivitiesMap.get(activity.id) || [];
          const name = (activity.name || "").toLowerCase();
          const isLegacyScreencast = name.includes("screencast");
          const isLegacyWebinar = name.includes("webinar");
          const isDiverse = isDiverseActivity(activity.name);
          actuals.forEach((aa: ActualActivity) => {
            const hours = aa.actualHours || 0;
            if (isDiverse) return;
            if (isLegacyScreencast && !isNewSystem) actualScreencasts += hours;
            else if (isLegacyWebinar && !isNewSystem) actualWebinars += hours;
            else if (!isLegacyScreencast && !isLegacyWebinar) actualActivities += hours;
          });
        });

        if (isNewSystem) {
          actualScreencasts = plannedScreencasts;
          actualWebinars = plannedWebinars;
        } else {
          // Old system: count from content-assignments for legacy screencast activities
          const assignmentKey = `${participantId}_${phase.id}`;
          (contentAssignmentsMap.get(assignmentKey) || []).forEach((a: any) => {
            if (a.completed || a.autoCompleteHours) {
              actualScreencasts += a.hourValue || 1;
            }
          });

          // Old system: count from event-attendees for legacy webinar activities
          const phaseStart = phase.start.toDate();
          const phaseEnd = phase.end.toDate();
          (eventAttendeesMap.get(participantId) || []).forEach((attendee: any) => {
            if (!attendee.present || !attendee.webinarId) return;
            const webinar = webinarsMap.get(attendee.webinarId);
            if (webinar?.start) {
              const webinarDate = webinar.start.toDate();
              if (webinarDate >= phaseStart && webinarDate <= phaseEnd) {
                actualWebinars += 2; // Default 2 hours per webinar
              }
            }
          });
        }

        return {
          actualActivities: Math.round(actualActivities),
          actualDiverse: Math.round(actualDiverse),
          actualScreencasts: Math.round(actualScreencasts),
          actualWebinars: Math.round(actualWebinars),
          plannedActivities: Math.round(plannedActivities),
          plannedDiverse: Math.round(plannedDiverse),
          plannedScreencasts: Math.round(plannedScreencasts),
          plannedWebinars: Math.round(plannedWebinars),
          totalActual: Math.round(actualActivities + actualDiverse + actualScreencasts + actualWebinars),
          totalPlanned: Math.round(plannedActivities + plannedDiverse + plannedScreencasts + plannedWebinars),
        };
      };

      const getPhaseText = (phaseIndex: number) => {
        const breakdown = getPhaseBreakdown(phaseIndex);
        if (!breakdown) return "";
        return `${breakdown.totalActual} / ${breakdown.totalPlanned}`;
      };

      const getHoursTotal = () => {
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        let totalActual = 0, totalPlanned = 0;
        phasesInThisProgram.forEach((_: Phase, i: number) => {
          const bd = getPhaseBreakdown(i);
          if (bd) {
            totalActual += bd.totalActual;
            totalPlanned += bd.totalPlanned;
          }
        });
        return `${isNaN(totalActual) ? 0 : totalActual} / ${totalPlanned}`;
      };

      const getFirstPhase = () => {
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        return phasesInThisProgram[0];
      };

      const getCurrentPhase = () => {
        let currentPhase = "Einde traject";
        const now = new Date().getTime();
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        for (const phase of phasesInThisProgram) {
          if (
            phase.start.toDate().getTime() < now &&
            phase.end.toDate().getTime() > now
          ) {
            currentPhase = `Fase ${phase.title.match(/\d+/)[0]}`;
            break;
          }
        }
        return currentPhase;
      };

      const getHoursPresent = () => {
        // return all hours where actualActivity.participantPresent is true
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        let hoursPresent = 0;
        phasesInThisProgram.forEach((phase: Phase) => {
          const activitiesForPhase = activitiesMap.get(phase.id) || [];
          activitiesForPhase.forEach((activity: Activity) => {
            const actualActivities = actualActivitiesMap.get(activity.id) || [];
            actualActivities.forEach((actualActivity: ActualActivity) => {
              if (actualActivity.participantPresent) {
                hoursPresent += actualActivity.actualHours;
              }
            });
          });
        });
        return isNaN(hoursPresent) ? 0 : Math.round(hoursPresent);
      };

      const getHoursConfirmed = () => {
        // return all hours where actualActivity.participantConfirmed is true
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        let hoursConfirmed = 0;
        phasesInThisProgram.forEach((phase: Phase) => {
          const activitiesForPhase = activitiesMap.get(phase.id) || [];
          activitiesForPhase.forEach((activity: Activity) => {
            const actualActivities = actualActivitiesMap.get(activity.id) || [];
            actualActivities.forEach((actualActivity: ActualActivity) => {
              if (actualActivity.participantConfirmed) {
                hoursConfirmed += actualActivity.actualHours;
              }
            });
          });
        });
        return isNaN(hoursConfirmed) ? 0 : Math.round(hoursConfirmed);
      };

      const getHoursNonConfirmed = () => {
        // return all hours where actualActivity.participantConfirmed is false
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        let hoursNonConfirmed = 0;
        phasesInThisProgram.forEach((phase: Phase) => {
          const activitiesForPhase = activitiesMap.get(phase.id) || [];
          activitiesForPhase.forEach((activity: Activity) => {
            const actualActivities = actualActivitiesMap.get(activity.id) || [];
            actualActivities.forEach((actualActivity: ActualActivity) => {
              if (
                actualActivity.participantPresent &&
                !actualActivity.participantConfirmed
              ) {
                hoursNonConfirmed += actualActivity.actualHours;
              }
            });
          });
        });
        return isNaN(hoursNonConfirmed) ? 0 : Math.round(hoursNonConfirmed);
      };

      const phaseIsWarning = (phaseIndex: number) => {
        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        if (phaseIndex >= phasesInThisProgram.length) return false;
        const phase = phasesInThisProgram[phaseIndex] as Phase;
        if (phase.end.toDate().getTime() > new Date().getTime()) return false;
        const breakdown = getPhaseBreakdown(phaseIndex);
        if (!breakdown || breakdown.totalPlanned === 0) return false;
        return breakdown.totalActual < breakdown.totalPlanned * 0.75;
      };

      const confirmedHoursWarning = () => {
        // return true if 20% or actual hours are not confirmed

        const phasesInThisProgram = phasesMap.get(programDoc.id) || [];
        let hoursPresent = 0;
        let hoursConfirmed = 0;
        phasesInThisProgram.forEach((phase: Phase) => {
          const activitiesForPhase = activitiesMap.get(phase.id) || [];
          activitiesForPhase.forEach((activity: Activity) => {
            const actualActivities = actualActivitiesMap.get(activity.id) || [];
            actualActivities.forEach((actualActivity: ActualActivity) => {
              if (actualActivity.participantPresent) {
                hoursPresent += actualActivity.actualHours;
              }
              if (actualActivity.participantConfirmed) {
                hoursConfirmed += actualActivity.actualHours;
              }
            });
          });
        });

        return hoursConfirmed / hoursPresent < 0.8;
      };

      this.rows.push({
        programId: programDoc.id,
        name: `${participant.firstname} ${participant.lastname}`,
        startDate: format(getFirstPhase().start.toDate(), "dd/MM/yyyy"),
        status: getCurrentPhase(),
        phase1: getPhaseText(0),
        phase2: getPhaseText(1),
        phase3: getPhaseText(2),
        phase1Warning: phaseIsWarning(0),
        phase2Warning: phaseIsWarning(1),
        phase3Warning: phaseIsWarning(2),
        phase1Breakdown: getPhaseBreakdown(0),
        phase2Breakdown: getPhaseBreakdown(1),
        phase3Breakdown: getPhaseBreakdown(2),
        totalHours: getHoursTotal(),
        present: getHoursPresent(),
        confirmed: getHoursConfirmed(),
        notConfirmed: getHoursNonConfirmed(),
        coach: `${coach.firstname} ${coach.lastname}`,
        confirmedHoursWarning: confirmedHoursWarning(),
        notes: programData.notes,
      });
    }
    this.loading = false;
  }

  getItemClass(item: any) {
    if (item.confirmedHoursWarning) {
      return "red lighten-5";
    }

    if (item.phase1Warning || item.phase2Warning || item.phase3Warning) {
      return "red lighten-5";
    }
  }

  exportAsCsv() {
    const header = Object.keys(this.rows[0]).join(",");
    const csv = this.rows.map((row) => Object.values(row).join(",")).join("\n");
    const blob = new Blob([header + "\n" + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "export.csv";
    a.click();
  }

  async updateNotes(programId: string, notes: string) {
    await updateDoc(doc(firestore, "programs", programId), {
      notes,
    });
  }

  mailDialog = false;
  mailProgram: Program | null = null;
  mailCoach: User | null = null;
  mailTemplate = null as string | null;
  mailSending = false;

  mailReasons = [
    {
      text: "Te weinig uren",
      value: "low_hours",
    },
    {
      text: "Activiteiten te bevestigen",
      value: "activities_to_confirm",
    },
    {
      text: "Te weinig uren & activiteiten te bevestigen",
      value: "low_hours_and_activities_to_confirm",
    },
  ];

  mailReason = null as string | null;

  async generateMailText() {
    if (!this.mailProgram) return;
    const programDoc = await getDoc(
      doc(firestore, "programs", this.mailProgram.id)
    );
    this.mailProgram = {
      ...programDoc.data(),
      id: programDoc.id,
    } as Program;
    const coachDoc = await getDoc(
      doc(firestore, "users", this.mailProgram.coach)
    );
    this.mailCoach = coachDoc.data() as User;
    const coach = coachDoc.data() as User;

    let problemText = "" as string;

    const isDutch = coach.language === "nl-be";
    const programTitle = this.mailProgram.title;
    const programId = this.mailProgram.id;
    const programLink = `https://hub.itinera-talentcoaching.be/coach/program/${programId}`;

    if (this.mailReason) {
      switch (this.mailReason) {
        case "low_hours":
          problemText = isDutch
            ? `Er lijken momenteel minder uren geregistreerd te zijn dan verwacht.`
            : `Il semble actuellement y avoir moins d'heures enregistrées que prévu.`;
          break;
        case "activities_to_confirm":
          problemText = isDutch
            ? `Sommige activiteiten zijn nog niet bevestigd.`
            : `Certaines activités ne sont pas encore confirmées.`;
          break;
        case "low_hours_and_activities_to_confirm":
          problemText = isDutch
            ? `Er lijken momenteel minder uren geregistreerd te zijn dan verwacht en sommige activiteiten zijn nog niet bevestigd.`
            : `Il semble actuellement y avoir moins d'heures enregistrées que prévu et certaines activités ne sont pas encore confirmées.`;
          break;
      }
    }

    const template = getMailTemplate({
      hello: isDutch ? "Dag" : "Bonjour",
      name: coach.firstname,
      question: isDutch ? "Vragen?" : "Des questions?",
      link: programLink,
      title: isDutch
        ? `Itinera Hub: programma ${programTitle}`
        : `Itinera Hub: programme ${programTitle}`,
      text: isDutch
        ? `Zou je het programma van ${programTitle} even kunnen nakijken?. ${problemText} Je kan dit makkelijk doen via onderstaande link.`
        : `Pourriez-vous vérifier le programme de ${programTitle} ? ${problemText} Vous pouvez le faire facilement en cliquant sur le lien ci-dessous.`,
      callToAction: isDutch ? "Naar Itinera Hub" : "Vers le Hub Itinera",
    });

    // send mail
    this.mailTemplate = template;
    this.mailDialog = true;
  }

  async openMailDialog(programId: string) {
    const programDoc = await getDoc(doc(firestore, "programs", programId));
    this.mailProgram = {
      ...programDoc.data(),
      id: programDoc.id,
    } as Program;
    await this.generateMailText();
    this.mailDialog = true;
  }

  async sendMail() {
    if (!this.mailProgram) return;
    if (!this.mailCoach) return;
    this.mailSending = true;

    await addDoc(collection(firestore, "mails"), {
      to: [this.mailCoach?.email],
      from: this.user?.email?.endsWith("@itinera-talentcoaching.be")
        ? this.user.email
        : "no-reply@itinera-talentcoaching.be",
      cc: ['erik.leenders@itinera-talentcoaching.be'],
      subject: "Itinera Hub: programma " + this.mailProgram.title,
      body: this.mailTemplate,
      sent: false,
      date: new Date(),
    });
    this.mailSending = false;
    this.mailDialog = false;
    this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
      color: "success",
      text: "Mail verzonden",
    });
  }

  @Watch("mailReason", { deep: true })
  onMailReasonChange() {
    this.generateMailText();
  }
}
